import { makeAutoObservable, runInAction } from 'mobx'

import { logger } from 'shared/lib'
import { TagsApi } from 'entities/Tags/api/tags'
import { Tag } from 'entities/Tags/model/Tag'
import type { IResponseTag } from 'entities/Tags'

export class TagsDropdownControlStore {
  static FromTags(tags: Tag[]) {
    const control = new TagsDropdownControlStore()

    tags.forEach((tag) => control._tagsMap.set(tag.id, tag))
    control._ids = Array.from(control._tagsMap.keys())
    control._synced = true

    return control
  }

  private _loading = false
  private _synced = false
  private _tagsMap = new Map<number, Tag>()

  constructor(private _ids: number[] = []) {
    this._synced = !_ids.length

    makeAutoObservable(this)
  }

  init = (tagsResponse: IResponseTag[]) => {
    this._tagsMap.clear()
    tagsResponse.forEach((tag) => this._tagsMap.set(tag.id, new Tag(tag)))
    this._ids = Array.from(this._tagsMap.keys())
    this._synced = true
  }

  get tagsMap() {
    return this._tagsMap
  }

  get loading() {
    return this._loading
  }

  get synced() {
    return this._synced
  }

  get ids() {
    return this._ids
  }

  get tags() {
    return Array.from(this._tagsMap.values())
  }

  get tagsCount() {
    return this._tagsMap.size
  }

  get hasTags() {
    return !!this._tagsMap.size
  }

  syncIds = async () => {
    if (this._synced) return

    try {
      runInAction(() => {
        this._loading = true
      })

      const tags = await TagsApi.getTagsById(this._ids)

      runInAction(() => {
        tags.forEach((item) => {
          const tagId = item.id
          const tag = new Tag(item)
          this._tagsMap.set(tagId, tag)
        })

        this._synced = true
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  addTag = (tag: Tag) => {
    if (this._tagsMap.has(tag.id)) return

    this._tagsMap.set(tag.id, tag)

    this._ids = Array.from(this._tagsMap.keys())
  }

  createTag = async (label: string) => {
    try {
      runInAction(() => {
        this._loading = true
      })

      const { data } = await TagsApi.createTag({ label })
      const tag = new Tag(data)

      this.addTag(tag)
      return tag
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  removeTag = (id: number) => {
    const deleted = this._tagsMap.delete(id)

    if (deleted) this._ids = Array.from(this._tagsMap.keys())
  }

  reset = () => {
    this._ids = []
    this._synced = true
    this._loading = false

    this._tagsMap.clear()
  }
}
